import { inject } from "@angular/core";
import * as Sentry from "@sentry/capacitor";
import { DatabaseService } from "src/app/services/database/database.service";
import { metricBaseAttributes } from "src/app/services/sentry-context/sentry-context.service";
import { environment } from "src/environments/environment";
import { appearanceEnum, msgCatStruct, targetPlatformEnum } from "./dataStruct";
import { getUtcTime, runCreateScripts } from "./functions";

import { NamedCommandOp, NamedCommandResult } from "../../services/database/named-command-batch";
const SQL_LARGE_PAYLOAD_BYTES = 5_000_000;
const SQL_LARGE_PAYLOAD_CATEGORY = "sql.payload.large";

// Cumulative-pressure telemetry. The 5 MB single-payload threshold doesn't catch SQL floods
// of many small queries — emit one breadcrumb per JS macrotask if the tick crossed either a
// byte or call-count threshold, attaching SQL prefixes to identify the offending loop.
const SQL_TICK_THRESHOLD_BYTES = 20_000_000;
const SQL_TICK_THRESHOLD_CALLS = 50;
const SQL_TICK_CATEGORY = "sql.payload.tick";
const SQL_TICK_MAX_PREFIXES = 10;

// The duty-signing `ident` is `<pin>-<aircraftRegistration>`, and the pin plus the
// mission limits is enough to forge a duty signature (focMain.ts:1319-1320, package_md5
// of `pin | sHash | missionLimitsJson`, sHash a hardcoded source constant). sentry.ts
// captures console breadcrumbs, and its SQL_NOISE_PATTERN covers `SQL-READ ::` /
// `SQL-WRITE ::` but not the `SQL:` prefix the two error paths below use.
//
// !! ON THIS BRANCH THE MASKER IS DEAD CODE, and that is the named-query catalog doing
// its job -- do not read its presence as evidence of a live leak here. All three signing
// statements are catalog entries (flightcrewByIdentLatest, flightcrewDutyByIdent,
// flightcrewCreate); namedQuery/namedCommand run them through prepareForPositional, so
// the ident and the signature travel as BOUND VALUES and never enter the SQL text.
// Nothing in database.service.ts logs those values on failure. Measured 2026-09-10:
// `ident='...'` appears nowhere in src outside com.spec.ts's own fixtures.
//
// It is kept for two reasons. It is the tripwire if a signing query is ever moved back
// to an interpolated sqlRunCmd, and cap-7 -- which has no catalog at all -- still needs
// it live. Keep the two copies in step.
//
// 🔴 cap-7 IS DIFFERENT AND ITS COMMENT SAYS SOMETHING ELSE. There the flightcrew INSERT
// really does interpolate, and the pin is recoverable from the logged signature; Igor
// accepted that on 2026-09-10. Do not copy either comment onto the other branch.
//
// !! Only the INSERT half actually reaches this masker. The two `ident='...'` SELECTs
// (focMain.ts:1199, :1381) pass ["efb_user"], so they run through
// DatabaseService.selectUser, which catches its own failure, logs the raw statement at
// `DbSrv: user db query failed` and returns [] -- sqlRunCmd's catch never fires for
// them. Masking the WHERE-clause shape here is therefore dead code kept for the day
// that path throws; the live read-side leak is in selectUser and is NOT closed here.
//
// ponytail: mask the pin only, keeping the registration. The registration is the half
// that makes a failing statement identifiable, and it is not a secret.
export function redactSigningIdent(sql: string, aircraftRegistration: string): string {
  if (!aircraftRegistration) return sql;
  const reg = aircraftRegistration.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  // Replacer must be a FUNCTION: as a string, a `$&` or `$1` inside the registration is
  // expanded by String.replace and re-emits the very pin this masks.
  return sql.replace(new RegExp("'[^']*-" + reg + "'", "g"), () => "'***-" + aircraftRegistration + "'");
}

let sqlTickBytes = 0;
let sqlTickCalls = 0;
let sqlTickPrefixes: string[] = [];
let sqlTickScheduled = false;

function recordSqlTickSample(prefix: string, approxBytes: number): void {
  sqlTickBytes += approxBytes;
  sqlTickCalls += 1;
  if (sqlTickPrefixes.length < SQL_TICK_MAX_PREFIXES) {
    sqlTickPrefixes.push(prefix.slice(0, 80));
  }

  if (sqlTickScheduled) return;
  sqlTickScheduled = true;
  setTimeout(() => {
    try {
      if (sqlTickBytes > SQL_TICK_THRESHOLD_BYTES || sqlTickCalls > SQL_TICK_THRESHOLD_CALLS) {
        Sentry.metrics.distribution("sql.tick.bytes", sqlTickBytes, { unit: "byte", attributes: metricBaseAttributes });
        Sentry.metrics.distribution("sql.tick.calls", sqlTickCalls, { attributes: metricBaseAttributes });
        Sentry.addBreadcrumb({
          category: SQL_TICK_CATEGORY,
          level: "warning",
          message: `sql tick: ${sqlTickCalls} calls, ~${sqlTickBytes} bytes`,
          data: { calls: sqlTickCalls, approxBytes: sqlTickBytes, samples: sqlTickPrefixes },
        });
      }
    } finally {
      sqlTickBytes = 0;
      sqlTickCalls = 0;
      sqlTickPrefixes = [];
      sqlTickScheduled = false;
    }
  }, 0);
}

export class opsCntrolCom {
  public appRestartRestorationDone: boolean = false;
  private database = inject(DatabaseService);

  public activeDutyDesig: string = "";
  public activeDutyId: string;
  public activePic: string;
  public activeHomebaseIdent: string;

  public aircraftRegistration: string = "";

  // Read-only by design: if a prod build emits SQL trace breadcrumbs (as seen in 1.0.70),
  // it means somebody flipped a mutable flag. Tied to environment.production directly
  // so tree-shaking strips the gated branches in prod builds.
  public readonly debugMode: boolean = !environment.production;
  public targetPlatform: targetPlatformEnum;
  public sqlInstance = undefined;
  public appearance: appearanceEnum;

  // message interface
  public lastMsgText: string = "";
  public lastMsgCat: msgCatStruct = msgCatStruct.nil;
  public msgErr: boolean = false;
  public msgTextList: string[] = [];
  public msgCattList: msgCatStruct[] = [];
  public msgTimestamp: Date[] = [];

  public package_open: any;
  public package_fs: any;
  public package_fetch: any;
  public package_md5: any;
  public package_sunriseSunset: any;
  public package_Showdown: any;

  // INIT Var
  public isApp: boolean = true;

  constructor() {}

  async init(
    mode: targetPlatformEnum,
    import_sqlite: any,
    appAppearance: appearanceEnum,
    package_fs: any,
    package_open: any,
    package_md5: any,
    package_Showdown: any,
    package_sunriseSunset: any
  ) {
    if (package_open != undefined) this.package_open = package_open;
    if (package_fs != undefined) this.package_fs = package_fs;
    if (package_md5 != undefined) this.package_md5 = package_md5;
    if (package_Showdown != undefined) this.package_Showdown = package_Showdown;
    if (package_sunriseSunset != undefined) this.package_sunriseSunset = package_sunriseSunset;

    this.targetPlatform = mode;
    console.log("platform set: " + targetPlatformEnum[mode]);
    console.log("targetPlatform: " + this.targetPlatform);

    this.appearance = appAppearance;

    switch (this.targetPlatform) {
      case targetPlatformEnum.server:
        if (import_sqlite !== undefined) {
          this.sqlInstance = import_sqlite;
        }
        break;

      case targetPlatformEnum.pwa:
      case targetPlatformEnum.na:
        console.log("targetPlatformEnum.pwa or native");
        if (import_sqlite !== undefined) {
          this.sqlInstance = import_sqlite;
        }
        break;
    }
  }

  public runCreateStmnts() {
    // run create smnt
    console.log("info: run create stmnts");
    const stmnts = runCreateScripts();
    for (let i = 0; i < stmnts.length; i++) {
      try {
        this.sqlRunCmd(stmnts[i], ["efb_user"]);
      } catch (err) {
        console.log("err: create stmnts #1214 " + err);
      }
    }
  }
  /**
   * Thin pass-through to the named-query catalog dispatcher so feature
   * classes built without DI (briefingFlowFeatures et al.) can reach the
   * process-DB read path without taking on a DatabaseService dependency.
   */
  async namedQuery(name: string, params: Record<string, unknown> = {}): Promise<any[]> {
    return this.database.namedQuery(name, params);
  }

  /** A named read whose failure throws on every platform. A native `namedQuery` of the user database answers `[]`. */
  async namedQueryOrThrow(name: string, params: Record<string, unknown> = {}): Promise<any[]> {
    return this.database.namedQuery(name, params, true);
  }

  /** Companion to namedQuery for catalog-dispatched writes. */
  async namedCommand(name: string, params: Record<string, unknown> = {}): Promise<void> {
    return this.database.namedCommand(name, params);
  }

  /** True where a write costs an HTTP round trip and accumulating pays. */
  get batchedWrites(): boolean {
    return this.database.batchedWrites;
  }

  /** Batched companion to namedCommand — one request for the whole list. */
  async namedCommandBatch(ops: NamedCommandOp[]): Promise<NamedCommandResult[]> {
    return this.database.namedCommandBatch(ops);
  }

  async sqlRunCmd(sqlCommand: string, servicename: string[] = [], filterServiceName?: string): Promise<any> {
    const trimmedSql = (sqlCommand ?? "").trim();

    if (!trimmedSql) {
      throw new Error("sqlRunCmd: empty SQL command");
    }

    const isWriteQuery =
      /^(insert|update|delete|replace|create|drop|alter|truncate|begin|commit|rollback|pragma|vacuum)\b/i.test(
        trimmedSql
      );

    switch (this.targetPlatform) {
      case targetPlatformEnum.server: {
        try {
          return await this.sqlInstance.query(trimmedSql);
        } catch (err: any) {
          const mask = (v: unknown) => redactSigningIdent(String(v ?? ""), this.aircraftRegistration);
          console.error("sqlRunCmd server error:");
          console.error("err:", mask(err));
          console.error("err.message:", mask(err?.message));
          console.error("err.stack:", mask(err?.stack));
          console.error("SQL:", mask(trimmedSql));
          throw err;
        }
      }

      case targetPlatformEnum.pwa:
      case targetPlatformEnum.na: {
        try {
          if (isWriteQuery) {
            if (!environment.production && this.debugMode) {
              console.log("--->");
              console.log("SQL-WRITE :: efb_user : " + trimmedSql);
              console.log("");
            }

            return await this.database.executeUser(trimmedSql);
          }

          let finalServiceNames: string[] = Array.isArray(servicename) ? [...servicename] : [];

          if (filterServiceName !== undefined && filterServiceName !== "") {
            const filtered = this.database.filterDbNames(filterServiceName) || [];
            finalServiceNames = [...new Set([...finalServiceNames, ...filtered])];
          }

          if (finalServiceNames.length === 0) {
            const msg = "err: TABLE NOT SELECTED / no matching service found! " + trimmedSql;
            console.error(msg);
            throw new Error(msg);
          }

          if (!environment.production && this.debugMode) {
            console.log("--->");
            console.log("base services:", servicename);
            console.log("filterServiceName:", filterServiceName);
            console.log("final services:", finalServiceNames);
            console.log("SQL-READ :: " + finalServiceNames.join(", ") + " : " + trimmedSql);
            console.log("");
          }

          const hasEfbUser = finalServiceNames.includes("efb_user");
          const processServices = finalServiceNames.filter((x) => x !== "efb_user");

          let data: any[] = [];

          if (hasEfbUser) {
            const userData = await this.database.selectUser(trimmedSql);
            data = data.concat(userData ?? []);
          }

          if (processServices.length > 0) {
            const processData = await this.database.selectProcesses(processServices, trimmedSql);
            data = data.concat(processData ?? []);
          }

          // Cheap O(cols) pre-check on the first row before paying for JSON.stringify on every read.
          if (data.length > 0) {
            try {
              let perRowApprox = 0;
              for (const v of Object.values(data[0])) {
                if (typeof v === "string") perRowApprox += v.length;
                else if (v != null) perRowApprox += 32;
              }
              const approxTotal = perRowApprox * data.length;
              recordSqlTickSample(trimmedSql, approxTotal);
              if (approxTotal > SQL_LARGE_PAYLOAD_BYTES / 2) {
                const payloadSize = JSON.stringify(data).length;
                if (payloadSize > SQL_LARGE_PAYLOAD_BYTES) {
                  const sqlPrefix = trimmedSql.slice(0, 200);
                  console.warn(`large sql payload: ${payloadSize} bytes :: ${sqlPrefix}`);
                  Sentry.metrics.distribution("sql.payload.bytes", payloadSize, {
                    unit: "byte",
                    attributes: metricBaseAttributes,
                  });
                  Sentry.addBreadcrumb({
                    category: SQL_LARGE_PAYLOAD_CATEGORY,
                    level: "warning",
                    message: sqlPrefix,
                    data: { bytes: payloadSize, services: finalServiceNames },
                  });
                }
              }
            } catch {
              // measurement only — never block the read
            }
          }

          return data;
        } catch (err: any) {
          const mask = (v: unknown) => redactSigningIdent(String(v ?? ""), this.aircraftRegistration);
          console.error("sqlRunCmd local db error:");
          console.error("err:", mask(err));
          console.error("err.message:", mask(err?.message));
          console.error("err.stack:", mask(err?.stack));
          console.error("SQL:", mask(trimmedSql));
          console.error("services:", servicename);
          console.error("filter:", filterServiceName);
          console.error("isWriteQuery:", isWriteQuery);
          throw err;
        }
      }

      default: {
        const msg = `Unsupported target platform: ${this.targetPlatform}`;
        console.error(msg);
        throw new Error(msg);
      }
    }
  }

  promptMessage(message: string, cat: msgCatStruct) {
    this.lastMsgText = message;

    if (cat == msgCatStruct.err) this.msgErr = true;

    // to avoid to resetting err state by mistake
    if (this.lastMsgCat !== msgCatStruct.err) {
      this.lastMsgCat = cat;
    }

    // push to msg lists
    this.msgCattList.push(cat);
    this.msgTextList.push(message);
    this.msgTimestamp.push(getUtcTime(new Date()));

    console.log(":: msgif: prompt message: " + message + " - " + msgCatStruct[cat]);
  }

  clearMessage() {
    this.lastMsgText = "";
    this.lastMsgCat = msgCatStruct.nil;
    this.msgErr = false;
    // push to msg lists
    this.msgCattList.push(msgCatStruct.nil);
    this.msgTextList.push("meessag cleared");
    this.msgTimestamp.push(getUtcTime(new Date()));

    //console.log(":: msgif: cleared message");
  }
}
